import { z } from 'zod';
import { CATALOG_CATEGORIES } from './catalog.js';
import { DateOnlySchema, IsoDateTimeSchema, UuidSchema, optionalText } from './common.js';

/**
 * Stock insights in the platform panel (ADR-056). Built every 15 minutes from the clinics' stock
 * movements into tables of their own; never a patient, a visit, a price, a supplier, a note or a
 * label UID. A clinic is counted only when the platform recorded its sharing basis.
 */

/** none: not used; aggregate: only in network totals; detailed: its own page too. */
export const DATA_SHARING_LEVELS = ['none', 'aggregate', 'detailed'] as const;
export type DataSharingLevel = (typeof DATA_SHARING_LEVELS)[number];

/** A product or brand row from fewer `aggregate` clinics than this (and no `detailed` one) is hidden. */
export const INSIGHTS_MIN_AGGREGATE_CLINICS = 3;

/** The longest period one request covers. */
export const INSIGHTS_MAX_RANGE_DAYS = 732;

export const DataSharingSchema = z.object({
  level: z.enum(DATA_SHARING_LEVELS),
  /** The contract or the accepted terms this rests on. */
  basis: z.string().nullable(),
  /** Stock history before this day is not used. */
  effectiveFrom: DateOnlySchema.nullable(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type DataSharing = z.infer<typeof DataSharingSchema>;

export const DataSharingInputSchema = z
  .object({
    level: z.enum(DATA_SHARING_LEVELS),
    basis: optionalText(200),
    effectiveFrom: DateOnlySchema.nullish().transform((v) => v ?? null),
  })
  .refine((v) => v.level === 'none' || v.basis !== null, {
    message: 'مبنای قراردادی را بنویسید (مثلاً شماره‌ی قرارداد).',
    path: ['basis'],
  })
  .refine((v) => v.level === 'none' || v.effectiveFrom !== null, {
    message: 'تاریخ شروع را انتخاب کنید.',
    path: ['effectiveFrom'],
  });
export type DataSharingInput = z.input<typeof DataSharingInputSchema>;

/** What each measure counts, by stock movement type (ADR-056 §3). `open_split` is never counted. */
export const INSIGHT_MEASURES = [
  'purchase',
  'opening',
  'adjustment_in',
  'adjustment_out',
  'consumption',
  'return_to_supplier',
  'damage',
  'expiry_writeoff',
] as const;
export type InsightMeasure = (typeof INSIGHT_MEASURES)[number];

/** Quantities of one row, one unit; decimal strings. */
const measures = {
  purchase: z.string(),
  opening: z.string(),
  adjustmentIn: z.string(),
  adjustmentOut: z.string(),
  consumption: z.string(),
  returnToSupplier: z.string(),
  damage: z.string(),
  expiryWriteoff: z.string(),
};

export const InsightsQuerySchema = z
  .object({
    from: DateOnlySchema,
    to: DateOnlySchema,
    brand: z.string().trim().min(1).max(80).optional(),
    productId: UuidSchema.optional(),
    category: z.enum(CATALOG_CATEGORIES).optional(),
    clinicId: UuidSchema.optional(),
  })
  .refine((v) => v.from <= v.to, { message: 'تاریخ شروع پس از تاریخ پایان است.', path: ['to'] })
  .refine((v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 < INSIGHTS_MAX_RANGE_DAYS, {
    message: 'بازه حداکثر دو سال است.',
    path: ['from'],
  });
export type InsightsQuery = z.infer<typeof InsightsQuerySchema>;

export const InsightsRunSchema = z.object({ finishedAt: IsoDateTimeSchema, ok: z.boolean() }).nullable();

/** Network totals of one unit. */
export const InsightsUnitTotalsSchema = z.object({ unit: z.string(), ...measures, onHand: z.string(), usable: z.string() });

/** One Jalali month (`1405-07`) of one unit. */
export const InsightsMonthSchema = z.object({ month: z.string(), unit: z.string(), ...measures });
export type InsightsMonth = z.infer<typeof InsightsMonthSchema>;

export const InsightsOverviewSchema = z.object({
  lastRun: InsightsRunSchema,
  clinics: z.object({
    members: z.number().int(),
    sharing: z.number().int(),
    withActiveStock: z.number().int(),
    withRecentActivity: z.number().int(),
  }),
  products: z.object({ distinct: z.number().int(), itemsWithoutCatalog: z.number().int() }),
  gtins: z.object({ approved: z.number().int(), pending: z.number().int() }),
  totals: z.array(InsightsUnitTotalsSchema),
  monthly: z.array(InsightsMonthSchema),
  topProducts: z.array(
    z.object({ productId: UuidSchema, brand: z.string(), model: z.string(), nameFa: z.string(), unit: z.string(), consumption: z.string(), clinicCount: z.number().int() }),
  ),
  topBrands: z.array(z.object({ brand: z.string(), unit: z.string(), consumption: z.string(), clinicCount: z.number().int() })),
});
export type InsightsOverview = z.infer<typeof InsightsOverviewSchema>;

export const InsightsProductRowSchema = z.object({
  productId: UuidSchema,
  gtins: z.array(z.string()),
  brand: z.string(),
  model: z.string(),
  nameFa: z.string(),
  category: z.enum(CATALOG_CATEGORIES),
  unit: z.string(),
  /** The same product is counted in more than one unit across clinics: never added together. */
  unitMismatch: z.boolean(),
  clinicCount: z.number().int(),
  ...measures,
  onHand: z.string(),
  usable: z.string(),
});
export type InsightsProductRow = z.infer<typeof InsightsProductRowSchema>;
export const InsightsProductListSchema = z.object({ items: z.array(InsightsProductRowSchema), truncated: z.boolean() });
export type InsightsProductList = z.infer<typeof InsightsProductListSchema>;

export const InsightsClinicSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  status: z.enum(['active', 'suspended']),
  sharing: DataSharingSchema,
});
export type InsightsClinic = z.infer<typeof InsightsClinicSchema>;
export const InsightsClinicListSchema = z.object({ items: z.array(InsightsClinicSchema) });

export const InsightsClinicItemSchema = z.object({
  itemId: UuidSchema,
  name: z.string(),
  nameEn: z.string().nullable(),
  unit: z.string(),
  gtin: z.string().nullable(),
  category: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  product: z.object({ id: UuidSchema, brand: z.string(), model: z.string(), nameFa: z.string() }).nullable(),
  onHand: z.string(),
  usable: z.string(),
  /** Opened vials kept for a client (no client is named). */
  keptForClients: z.string(),
  nearExpiry: z.string(),
  expired: z.string(),
  nearestExpiry: DateOnlySchema.nullable(),
  isLow: z.boolean(),
  ...measures,
  /** Consumption of the same length of time just before `from`. */
  previousConsumption: z.string(),
});
export type InsightsClinicItem = z.infer<typeof InsightsClinicItemSchema>;

export const InsightsClinicDetailSchema = z.object({
  clinic: InsightsClinicSchema,
  lastRun: InsightsRunSchema,
  items: z.array(InsightsClinicItemSchema),
  monthly: z.array(InsightsMonthSchema),
});
export type InsightsClinicDetail = z.infer<typeof InsightsClinicDetailSchema>;

export const InsightsItemSeriesSchema = z.object({
  item: InsightsClinicItemSchema,
  /** Per Jalali month: what came in and went out, and the stock at the month's end. */
  months: z.array(z.object({ month: z.string(), ...measures, closing: z.string() })),
});
export type InsightsItemSeries = z.infer<typeof InsightsItemSeriesSchema>;

export const InsightsExportQuerySchema = z.intersection(
  InsightsQuerySchema,
  z.object({ scope: z.enum(['network', 'clinic']) }),
);
